import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { STAFF_MEMBERS, SUPERADMIN_STAFF, StaffMember } from '@/constants/staff';
import { Button } from '@/components/ui/button';

interface StaffSelectorProps {
  onSelectStaff: (staff: StaffMember) => void;
  isSuperAdmin?: boolean;
  onBack?: () => void;
}

const StaffSelector: React.FC<StaffSelectorProps> = ({ onSelectStaff, isSuperAdmin = false, onBack }) => {
  // Si es superadmin, mostrar SOLO a Rubén
  const staffToShow = isSuperAdmin ? [SUPERADMIN_STAFF] : STAFF_MEMBERS;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4 relative">
      {onBack && (
        <Button
          variant="ghost"
          onClick={onBack}
          className="absolute top-4 left-4 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
      )}
      <div className="bg-card rounded-2xl shadow-xl p-8 max-w-4xl w-full border border-border">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {isSuperAdmin ? '¡Bienvenido, Jefe! 👑' : '¡Bienvenido! 👋'}
          </h1>
          <p className="text-muted-foreground">
            {isSuperAdmin ? 'Acceso de Gerencia' : 'Selecciona tu usuario para continuar'}
          </p>
        </div>

        <div className={`grid ${isSuperAdmin ? 'grid-cols-1 max-w-xs mx-auto' : 'grid-cols-2 md:grid-cols-3'} gap-4`}>
          {staffToShow.map((staff) => (
            <button
              key={staff.id}
              onClick={() => onSelectStaff(staff)}
              className="group relative bg-gradient-to-br from-card to-muted/30 rounded-xl p-6 border-2 border-border hover:border-primary hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="flex flex-col items-center space-y-3">
                {staff.avatar ? (
                  <img
                    src={staff.avatar}
                    alt={staff.name}
                    className="w-20 h-20 rounded-full object-cover ring-4 ring-background group-hover:ring-primary/20 transition-all"
                  />
                ) : (
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold ring-4 ring-background group-hover:ring-opacity-50 transition-all"
                    style={{ backgroundColor: staff.color }}
                  >
                    {staff.initials}
                  </div>
                )}
                <div className="text-center">
                  <p className="font-semibold text-foreground text-lg">
                    {staff.name}
                  </p>
                  {staff.role && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {staff.role}
                    </p>
                  )}
                </div>
              </div>
              <div className="absolute inset-0 bg-primary opacity-0 group-hover:opacity-5 rounded-xl transition-opacity" />
            </button>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            {isSuperAdmin ? '🔐 Acceso con privilegios de administrador' : '💡 Tip: Esta selección quedará guardada durante tu sesión'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StaffSelector;
